import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { SubscriptionPlansCarousel } from '@/modules/portal/components/subscription-plans/SubscriptionPlansCarousel';
import { PortalAddonsSection } from '@/modules/portal/components/subscription-plans/PortalAddonsSection';
import { PortalGuidanceBanner } from '@/modules/portal/components/PortalGuidanceBanner';
import { PortalReadinessBadge } from '@/modules/portal/components/PortalReadinessBadge';
import {
  PortalEmptyValue,
  PortalSubscriptionStatusBadge,
  PortalSystemAccessBadge,
} from '@/modules/portal/components/PortalStatusBadges';
import { PortalDetailGrid } from '@/modules/portal/components/ui/PortalDetailGrid';
import { PortalKpiCard } from '@/modules/portal/components/ui/PortalKpiCard';
import { PortalPage } from '@/modules/portal/components/ui/PortalPage';
import { PortalQuickActionCard } from '@/modules/portal/components/ui/PortalQuickActionCard';
import { PortalLoadingState, portalSpinClass } from '@/modules/portal/components/ui/portal-loading';
import { PortalFadeIn, PortalStagger } from '@/modules/portal/components/ui/portal-motion';
import { fetchPortalOverview } from '@/modules/portal/services/portalOverview.api';
import {
  buildPortalSupportMailto,
  buildPortalSupportMailtoLabels,
  derivePortalUpgradeReadiness,
  formatPortalProductPlan,
} from '@/modules/portal/utils/portalSelfServiceHelpers';
import { Button, buttonVariants } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';

export function PortalUpgradePage() {
  const { t, i18n } = useTranslation();

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['me', 'portal-overview'],
    queryFn: fetchPortalOverview,
  });

  if (isLoading) {
    return (
      <PortalLoadingState
        size="md"
        message={t('common.loading')}
        className="min-h-[280px] justify-center"
      />
    );
  }

  if (error) {
    return (
      <div className="portal-glass-panel mx-auto max-w-lg space-y-4 rounded-2xl border-destructive/30 p-6">
        <h2 className="text-lg font-semibold text-foreground">{t('portal.upgrade.error.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('portal.upgrade.error.message')}</p>
        <Button type="button" variant="outline" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const hasSubscription = data.subscription_id !== null;
  const readiness = derivePortalUpgradeReadiness(data);
  const productPlan = formatPortalProductPlan(data);

  const supportMailto = buildPortalSupportMailto(
    data,
    'upgrade',
    buildPortalSupportMailtoLabels(data, 'upgrade', t),
  );

  const contextItems = hasSubscription
    ? [
        {
          id: 'company',
          label: t('portal.upgrade.fields.company'),
          value: data.tenant_name || <PortalEmptyValue />,
        },
        {
          id: 'product-plan',
          label: t('portal.upgrade.fields.productPlan'),
          value: productPlan ?? <PortalEmptyValue />,
        },
        {
          id: 'status',
          label: t('portal.upgrade.fields.subscriptionStatus'),
          value: <PortalSubscriptionStatusBadge status={data.subscription_status} />,
        },
        {
          id: 'system-access',
          label: t('portal.upgrade.fields.systemAccess'),
          value: <PortalSystemAccessBadge status={data.system_access_status} />,
        },
        {
          id: 'readiness',
          label: t('portal.upgrade.fields.upgradeReadiness'),
          value: <PortalReadinessBadge namespace="upgrade" state={readiness} />,
        },
      ]
    : [];

  return (
    <PortalPage
      title={t('portal.pages.upgrade.title')}
      description={t('portal.upgrade.description')}
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isFetching}
          onClick={() => void refetch()}
          className="rounded-xl"
        >
          <Icons.RefreshCw className={cn('me-2 h-4 w-4', isFetching && portalSpinClass)} />
          {t('common.refresh')}
        </Button>
      }
    >
      {!hasSubscription ? (
        <PortalFadeIn>
          <div className="portal-empty-state portal-glass-panel max-w-lg">
            <Icons.Layers className="mx-auto h-10 w-10 text-muted-foreground/50" aria-hidden />
            <p className="mt-4 text-lg font-semibold text-foreground">
              {t('portal.upgrade.empty.title')}
            </p>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              {t('portal.upgrade.empty.message')}
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Link
                to="/portal/subscription"
                className={buttonVariants({ variant: 'outline', className: 'rounded-xl' })}
              >
                {t('portal.upgrade.actions.backToSubscription')}
              </Link>
              <a
                href={supportMailto}
                className={buttonVariants({ variant: 'default', className: 'rounded-xl' })}
              >
                {t('portal.upgrade.actions.contactSupport')}
              </a>
            </div>
          </div>
        </PortalFadeIn>
      ) : (
        <>
          <PortalGuidanceBanner
            title={t('portal.upgrade.guidance.title')}
            message={t('portal.upgrade.guidance.message')}
            icon={<Icons.Layers className="h-5 w-5" />}
            variant={readiness === 'access_restricted' ? 'warning' : 'info'}
          />

          <PortalStagger className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <PortalKpiCard label={t('portal.upgrade.cards.currentPlan')} icon={Icons.Package}>
              {productPlan ? (
                <p className="text-sm font-medium leading-snug text-foreground">{productPlan}</p>
              ) : (
                <PortalEmptyValue />
              )}
            </PortalKpiCard>
            <PortalKpiCard label={t('portal.upgrade.cards.systemAccess')} icon={Icons.CheckCircle}>
              <PortalSystemAccessBadge status={data.system_access_status} />
            </PortalKpiCard>
          </PortalStagger>

          <PortalDetailGrid
            title={t('portal.upgrade.sections.context')}
            description={t('portal.upgrade.sections.contextHint')}
            items={contextItems}
          />

          <SubscriptionPlansCarousel enabled={hasSubscription} />

          <PortalAddonsSection locale={i18n.language} />

          <section className="space-y-4">
            <h2 className="text-base font-semibold text-foreground">
              {t('portal.upgrade.actions.title')}
            </h2>
            <p className="text-sm text-muted-foreground">{t('portal.upgrade.actions.hint')}</p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <a
                href={supportMailto}
                className={buttonVariants({ variant: 'default', className: 'rounded-xl' })}
              >
                {t('portal.upgrade.actions.contactSupport')}
              </a>
              <Link
                to="/portal/subscription"
                className={buttonVariants({ variant: 'outline', className: 'rounded-xl' })}
              >
                {t('portal.upgrade.actions.backToSubscription')}
              </Link>
            </div>
            <PortalStagger className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <PortalQuickActionCard
                to="/portal/subscription"
                label={t('portal.upgrade.actions.viewPlanDetails')}
                description={t('portal.upgrade.actions.viewPlanDetailsHint')}
                icon={Icons.Repeat}
              />
              <PortalQuickActionCard
                to="/portal/subscription"
                label={t('portal.upgrade.actions.backToSubscription')}
                description={t('portal.upgrade.actions.backToSubscriptionHint')}
                icon={Icons.Layers}
              />
            </PortalStagger>
          </section>

          <p className="text-center text-xs text-muted-foreground">
            {t('portal.upgrade.safeNote')}
          </p>
        </>
      )}
    </PortalPage>
  );
}
